import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { StatCard } from "@/lib/ui";

const fmtBytes = (b) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(2)} GB` : b >= 1024 ** 2 ? `${(b / 1024 ** 2).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

function StorageCard({ s }) {
  const pct = Math.min(s.percent, 100);
  const tone = pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <>
      <h3 className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Storage</h3>
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="dash-storage-card">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500"><i className="fa-solid fa-hard-drive mr-1 text-orange-600" /> Image storage used</p>
            <p className="font-head text-2xl font-extrabold text-slate-900" data-testid="dash-storage-used">{fmtBytes(s.used_bytes)} <span className="text-sm font-semibold text-slate-400">of {fmtBytes(s.quota_bytes)} ({s.percent}%)</span></p>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>{s.files} files uploaded{s.untracked_files ? ` · ${s.untracked_files} older files not measured` : ""}</p>
            <p className="text-emerald-700">Saved {fmtBytes(s.saved_bytes)} via auto-compression</p>
            <p>Database: {fmtBytes(s.db_bytes)}</p>
          </div>
        </div>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${tone} transition-all`} style={{ width: `${Math.max(pct, 0.5)}%` }} /></div>
        <p className="mt-2 text-[11px] text-slate-400">Images are auto-resized to max 1200px JPEG on upload. Quota is 5 GB per Emergent organization.</p>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <p className="text-slate-400">Loading…</p>;

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Admin Dashboard</h1>

      <h3 className="mt-5 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Users & Products</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        <StatCard label="Retailers" value={d.sellers.retailers} icon="fa-store" />
        <StatCard label="Wholesalers" value={d.sellers.wholesalers} icon="fa-warehouse" />
        <StatCard label="Buyers" value={d.sellers.buyers} icon="fa-user" />
        <StatCard label="Active Subs" value={d.sellers.active_subs} icon="fa-crown" tone="green" />
        <StatCard label="Active Products" value={d.products.active} icon="fa-box" tone="green" />
        <StatCard label="Pending" value={d.products.pending} icon="fa-clock" tone="amber" testid="dash-pending-count" />
      </div>

      <h3 className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Listings & Engagement</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
        <StatCard label="Manufacturer Stock" value={d.products.manufacturer} icon="fa-industry" tone="blue" />
        <StatCard label="Pan India" value={d.products.pan_india} icon="fa-truck-fast" tone="orange" />
        <StatCard label="Product Views" value={d.engagement.product_views} icon="fa-eye" tone="blue" />
        <StatCard label="WhatsApp Clicks" value={d.engagement.whatsapp_clicks} icon="fa-whatsapp" tone="green" />
        <StatCard label="Restricted Attempts" value={d.engagement.restricted_attempts} icon="fa-ban" tone="red" />
      </div>

      <h3 className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Revenue</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Subscription" value={money(d.revenue.subscription)} tone="green" />
        <StatCard label="Instagram" value={money(d.revenue.instagram)} tone="blue" />
        <StatCard label="Badges" value={money(d.revenue.badge)} tone="amber" />
        <StatCard label="Total Revenue" value={money(d.revenue.total)} tone="green" testid="dash-total-revenue" />
      </div>

      {d.storage && <StorageCard s={d.storage} />}
    </div>
  );
}
